import React, { useCallback, useEffect, useState } from "react";
import SwapService from "../../services/Swap/swapService";
import { statusLabel as swapStatusLabel } from "./statusLabels";

const SIDE_LABEL = {
  buy: "Compra",
  sell: "Venda",
};

// Estados possíveis em `lockedOperation.status` depois de `POST
// /quotes/{id}/accept` (Higher-API, SwapStateMachine.cs): `Quoted +
// AcceptUnconfirmed → LockUncertain` quando a mesa não confirma a tempo — é
// sucesso (200), não exceção, mas sem instrução de depósito. Só a partir de
// `AwaitingDeposit` (via `Quoted + Accept`) existe endereço para mostrar.
// "Trava em dúvida" é o mesmo rótulo de `OperationHistory.jsx`.
const LOCK_UNCERTAIN_STATUS = "LockUncertain";
const DEPOSIT_READY_STATUSES = new Set([
  "AwaitingDeposit",
  "AwaitingSettlement",
  "Completed",
]);

// `lockedOperation` só existe depois de um 200 de accept, mas o status ali
// dentro cobre a família toda do SwapStatus (ver vocabulário em
// `OperationHistory.jsx`): `Quoted`/`Locked` (mesa ainda não decidiu),
// `Expired`/`Cancelled` (cotação morreu depois do accept) também chegam
// assim. Enumerar cada um desses é o que apagou a tela antes — qualquer
// status fora dos dois conjuntos tratados acima cai neste fallback, no
// mesmo padrão do 409 de `isDeadQuoteError`: mensagem + `onRequestNewQuote`.
function isUnknownLockedStatus(status) {
  return status !== LOCK_UNCERTAIN_STATUS && !DEPOSIT_READY_STATUSES.has(status);
}

// 409 que significam cotação morta: reenviar o mesmo código de 2FA só
// repete o erro, então oferecemos o caminho de frente (nova cotação) em vez
// de deixar o formulário aberto contra uma cotação que não existe mais.
function isDeadQuoteError(error) {
  return error?.response?.status === 409;
}

function sideLabel(side) {
  return SIDE_LABEL[String(side).toLowerCase()] || side;
}

function formatCountdown(remainingMs) {
  const totalSeconds = Math.max(0, Math.floor(remainingMs / 1000));
  const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

/**
 * Mostra a cotação recém-criada (par, lado, quantidade, preço final) com
 * contagem regressiva até `quote.expiresAt`. Ao zerar, o botão de travar
 * desabilita e o card pede nova cotação via `onRequestNewQuote`.
 *
 * A trava abre o campo de 2FA de seis dígitos no padrão já usado em
 * `src/pages/Ramps/index.jsx` (maxLength=6, `replace(/\D/g, "")`, Enter ao
 * completar) e envia o código no corpo de `POST /quotes/{id}/accept`, nunca
 * na query. Depois da trava, mostra os dados de depósito e a instrução que
 * vieram na resposta.
 */
/**
 * Preço da cotação com 10 casas decimais, completando com zero quando vier menos.
 *
 * Dez casas porque é a precisão em que a mesa cota, e arredondar para duas na tela
 * escondia a diferença entre dois preços que o cliente compara — 5,15 e 5,15 podem
 * ser 5,1512345678 e 5,1587654321, e a decisão de travar é tomada nesse dígito.
 *
 * Trabalha sobre o TEXTO quando o preço vem como texto: converter para número antes
 * de formatar desfaz justamente a precisão que este formato existe para mostrar. Só
 * cai em `Number` quando o valor não é um decimal reconhecível.
 */
export function formatQuotePrice(value) {
  const raw = String(value ?? "").trim();

  /*
    Vazio é AUSENTE, não zero. `Number("")` é 0, e um preço que não veio exibido como
    0,0000000000 é uma mentira silenciosa numa tela onde o número decide a trava.
  */
  if (!raw) return "-";

  const match = /^(-?\d+)(?:[.,](\d*))?$/.exec(raw);

  if (match) {
    const [, whole, decimals = ""] = match;
    return `${whole}.${decimals.padEnd(10, "0").slice(0, 10)}`;
  }

  const asNumber = Number(raw);
  return Number.isFinite(asNumber) ? asNumber.toFixed(10) : "-";
}

function QuoteCard({ quote, email, onRequestNewQuote, onLocked }) {
  const [remainingMs, setRemainingMs] = useState(0);
  const [showTwoFactor, setShowTwoFactor] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");
  const [locking, setLocking] = useState(false);
  const [lockError, setLockError] = useState("");
  const [lockErrorIsDeadQuote, setLockErrorIsDeadQuote] = useState(false);
  const [lockedOperation, setLockedOperation] = useState(null);

  useEffect(() => {
    setShowTwoFactor(false);
    setTwoFactorCode("");
    setLockError("");
    setLockErrorIsDeadQuote(false);
    setLockedOperation(null);
  }, [quote?.id]);

  useEffect(() => {
    if (!quote?.expiresAt) {
      setRemainingMs(0);
      return undefined;
    }

    const expiresAtMs = new Date(quote.expiresAt).getTime();
    const tick = () => setRemainingMs(Math.max(0, expiresAtMs - Date.now()));

    tick();
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [quote?.expiresAt, quote?.id]);

  const expired = remainingMs <= 0;

  const openTwoFactor = () => {
    if (expired || lockedOperation) return;
    setLockError("");
    setShowTwoFactor(true);
  };

  const cancelTwoFactor = () => {
    setShowTwoFactor(false);
    setTwoFactorCode("");
    setLockError("");
    setLockErrorIsDeadQuote(false);
  };

  const handleTwoFactorChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").substring(0, 6);
    setTwoFactorCode(value);
  };

  const lockQuote = useCallback(async () => {
    if (twoFactorCode.length !== 6 || locking || !quote) return;

    setLocking(true);
    setLockError("");
    setLockErrorIsDeadQuote(false);
    try {
      const response = await SwapService.acceptQuote(quote.id, {
        email,
        twoFactorCode,
      });
      setLockedOperation(response.data);
      setShowTwoFactor(false);
      setTwoFactorCode("");
      if (onLocked) onLocked(response.data);
    } catch (error) {
      setLockError(
        error?.response?.data?.message ||
          "Não foi possível travar a cotação.",
      );
      setLockErrorIsDeadQuote(isDeadQuoteError(error));
    } finally {
      setLocking(false);
    }
  }, [quote, email, twoFactorCode, locking, onLocked]);

  const handleEnterTwoFactor = (event) => {
    if (event.key === "Enter" && twoFactorCode.length === 6) {
      lockQuote();
    }
  };

  if (!quote) return null;

  return (
    <section className="swapCard" id="swapQuoteCard">
      <p className="swapCard__title">Cotação</p>

      {/* O preço vem primeiro e grande: é o dado pelo qual o cliente decide, e
          antes ele estava numa linha de texto igual às outras. */}
      <p className="swapQuote__priceLabel">Preço final</p>
      <p className="swapQuote__price">{formatQuotePrice(quote.price)}</p>

      <dl className="swapQuote__meta">
        <div className="swapQuote__metaItem">
          <dt>Par</dt>
          <dd>{quote.pair}</dd>
        </div>
        <div className="swapQuote__metaItem">
          <dt>Lado</dt>
          <dd>{sideLabel(quote.side)}</dd>
        </div>
        <div className="swapQuote__metaItem">
          <dt>Quantidade</dt>
          <dd>{quote.quantity}</dd>
        </div>
      </dl>

      {!lockedOperation && (
        <div>
          {/* A classe do cronômetro acompanha o prazo: discreto, depois âmbar
              abaixo de 10s, depois vermelho ao vencer. É aviso, não enfeite —
              o preço na tela está morrendo. */}
          <p
            className={
              "swapTimer" +
              (expired
                ? " swapTimer--expired"
                : remainingMs <= 10000
                  ? " swapTimer--urgent"
                  : "")
            }
          >
            Validade: <strong>{formatCountdown(remainingMs)}</strong>
          </p>

          {expired && (
            <div className="swapNotice swapNotice--danger">
              <p>Cotação expirada. O preço acima não vale mais.</p>
              <div className="swapFormActions">
                <button
                  type="button"
                  className="swapBtn swapBtn--primary"
                  onClick={onRequestNewQuote}
                >
                  Nova cotação
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!lockedOperation && !showTwoFactor && (
        <div className="swapFormActions">
          <button
            type="button"
            className="swapBtn swapBtn--primary"
            onClick={openTwoFactor}
            disabled={expired}
          >
            Travar cotação
          </button>
        </div>
      )}

      {!lockedOperation && showTwoFactor && (
        <div className="swapNotice swapNotice--warn">
          <p>Digite o código de 6 dígitos para travar a cotação:</p>

          <div className="swapField">
            <label className="swapLabel" htmlFor="swapTwoFactor">
              Código do segundo fator
            </label>
            <input
              id="swapTwoFactor"
              className="swapInput swapInput--code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={twoFactorCode}
              onChange={handleTwoFactorChange}
              onKeyDown={handleEnterTwoFactor}
              maxLength="6"
              placeholder="000000"
              disabled={locking}
            />
          </div>

          {lockError && <p className="swapError">{lockError}</p>}

          <div className="swapFormActions">
            <button
              type="button"
              className="swapBtn swapBtn--ghost"
              onClick={cancelTwoFactor}
              disabled={locking}
            >
              Cancelar
            </button>
            <button
              type="button"
              className="swapBtn swapBtn--primary"
              onClick={lockQuote}
              disabled={locking || twoFactorCode.length !== 6 || expired}
            >
              {locking ? "Travando..." : "Confirmar"}
            </button>
            {lockErrorIsDeadQuote && (
              <button
                type="button"
                className="swapBtn swapBtn--ghost"
                onClick={onRequestNewQuote}
              >
                Nova cotação
              </button>
            )}
          </div>
        </div>
      )}

      {lockedOperation && lockedOperation.status === LOCK_UNCERTAIN_STATUS && (
        <div className="swapNotice swapNotice--warn">
          <p>
            <strong>Trava em dúvida.</strong>
          </p>
          <p>
            A confirmação está pendente e pode levar alguns minutos. Acompanhe o
            andamento pelo histórico de operações.
          </p>
        </div>
      )}

      {lockedOperation &&
        DEPOSIT_READY_STATUSES.has(lockedOperation.status) && (
          <div className="swapNotice swapNotice--ok">
            <p>
              <strong>Cotação travada com sucesso.</strong>
            </p>

            {/* Bloco copiável e monoespaçado: é para onde o dinheiro vai.
                Quebra em qualquer ponto porque endereço de cripto não tem
                espaço, e cortado ele manda para o lugar errado. */}
            <p className="swapDeposit__key">Instrução de depósito</p>
            <p className="swapDeposit__value">
              {lockedOperation.deposit || "-"}
            </p>

            <p className="swapDeposit__key">Rede</p>
            <p className="swapDeposit__value">
              {lockedOperation.network || "-"}
            </p>
          </div>
        )}

      {lockedOperation && isUnknownLockedStatus(lockedOperation.status) && (
        <div className="swapNotice swapNotice--danger">
          <p>
            {`Status atual: ${swapStatusLabel(
              lockedOperation.status,
            )}. Esta cotação não está mais disponível para depósito.`}
          </p>
          <div className="swapFormActions">
            <button
              type="button"
              className="swapBtn swapBtn--primary"
              onClick={onRequestNewQuote}
            >
              Nova cotação
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export default QuoteCard;
